import React, { useState, useEffect } from 'react';
import { api } from '../api';

/**
 * Email server status.
 *
 * Every email the app sends goes through one Zoho SMTP connection, and when
 * it breaks the symptom is always the same useless one: the app says "sent"
 * and nothing arrives. This shows which host and port are actually live, and
 * whether test mode is quietly redirecting everything to one mailbox.
 */

const row = { display: 'grid', gridTemplateColumns: '150px 1fr', gap: '6px 14px', fontSize: 13.5, alignItems: 'baseline' };
const key = { color: '#6b7280', fontWeight: 600 };
const val = { fontWeight: 700, color: '#1a1a2e', wordBreak: 'break-all' };

export default function EmailServerSettings() {
  const [status,  setStatus]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [to,      setTo]      = useState('');
  const [sending, setSending] = useState(false);
  const [result,  setResult]  = useState(null);

  useEffect(() => { check(); }, []);

  async function check() {
    setLoading(true); setResult(null);
    try { setStatus(await api.getEmailStatus()); }
    catch (e) { setStatus({ ok: false, error: e.message }); }
    finally { setLoading(false); }
  }

  async function sendTest() {
    setSending(true); setResult(null);
    try {
      const r = await api.sendTestEmail(to.trim() || undefined);
      setResult({
        ok: true,
        msg: r.testMode
          ? `EMAIL_TEST_MODE is ON — the email went to ${(r.to || []).join(', ')}, not to the address above.`
          : `Sent to ${(r.to || []).join(', ')}. If it doesn't arrive within a few minutes, check the spam folder — SMTP accepted it, so it left the server.`,
      });
    } catch (e) {
      setResult({ ok: false, msg: e.message });
    } finally { setSending(false); }
  }

  const ok = status?.ok;

  return (
    <div style={{ padding: 24, maxWidth: 620 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 800, color: '#1a1a2e' }}>Email server</h3>
      <p style={{ margin: '0 0 18px', fontSize: 13, color: '#6b7280' }}>
        Invoices, buffing authorisations and the insurance digest all go out through here.
      </p>

      {loading ? (
        <div style={{ color: '#6b7280', fontSize: 13.5 }}>Checking…</div>
      ) : (
        <>
          <div style={{
            background: ok ? '#dcfce7' : '#fee2e2', color: ok ? '#15803d' : '#b91c1c',
            borderRadius: 6, padding: '12px 14px', fontSize: 14, fontWeight: 800, marginBottom: 14,
          }}>
            {ok ? 'Connected' : `Not connected — ${status?.error || status?.message || 'unknown error'}`}
          </div>

          {/* What the server is actually holding, without printing it. "Invalid
              client secret" otherwise gives no way to tell a wrong paste from
              a truncated one. */}
          {!ok && status?.secret_hint && (
            <div style={{ ...row, marginBottom: 14 }}>
              <span style={key}>Secret in use</span>
              <span style={{ ...val, fontFamily: 'monospace' }}>{status.secret_hint}</span>
            </div>
          )}

          {ok && (
            <div style={{ ...row, marginBottom: 16 }}>
              <span style={key}>Sending through</span>
              <span style={val}>
                {status.transport || 'SMTP'}
                {status.port ? ` · ${status.host}:${status.port}` : ''}
              </span>
              {status.env_port != null && status.env_port !== status.port && (
                <>
                  <span style={key}>Configured port</span>
                  <span style={{ ...val, color: '#8a6410' }}>
                    {status.env_port} — unreachable, using {status.port} instead
                  </span>
                </>
              )}
              <span style={key}>Mailbox</span>
              <span style={val}>{status.user || status.fromAddr}</span>
              <span style={key}>Sends as</span>
              <span style={val}>{status.fromName} &lt;{status.fromAddr}&gt;</span>
              <span style={key}>Test mode</span>
              <span style={{ ...val, color: status.testMode ? '#b91c1c' : '#15803d' }}>
                {status.testMode
                  ? 'ON — every email is redirected, nothing reaches the real recipient'
                  : 'Off'}
              </span>
            </div>
          )}

          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8 }}>Send a test email</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                value={to} onChange={e => setTo(e.target.value)}
                placeholder={status?.user || 'your@email.com'}
                style={{ flex: 1, minWidth: 200, padding: '9px 11px', border: '1.5px solid #dfe3e7', borderRadius: 4, fontSize: 13.5 }}
              />
              <button onClick={sendTest} disabled={sending} style={{
                padding: '9px 18px', borderRadius: 4, border: 'none', fontSize: 13.5, fontWeight: 700,
                background: sending ? '#9ca3af' : '#4A7A91', color: '#fff', cursor: sending ? 'wait' : 'pointer',
              }}>{sending ? 'Sending…' : 'Send test'}</button>
              <button onClick={check} style={{
                padding: '9px 18px', borderRadius: 4, border: '1.5px solid #dfe3e7',
                background: '#fff', fontSize: 13.5, fontWeight: 700, color: '#374151', cursor: 'pointer',
              }}>Re-check</button>
            </div>

            {result && (
              <div style={{
                marginTop: 12, borderRadius: 6, padding: '10px 12px', fontSize: 13, fontWeight: 600,
                background: result.ok ? '#dcfce7' : '#fee2e2', color: result.ok ? '#15803d' : '#b91c1c',
              }}>
                {result.msg}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
